import { PageWrapper } from '../../components/PageWrapper';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import OtpInput from 'react18-input-otp';
import {
  PHONE_PASSWORD_LOGIN,
  PHONE_PASSWORD_CREATE,
  HOME,
} from '../../constants/routes';
import { handleOtpVerification, formatPhoneNumber } from '../../utils/phoneNumber';
import { auth } from '../../firebase/firebaseConfig';
import { phoneNumberSignUp } from '../../pages/account/phoneNumberLogin/services/phoneAuthService';
import { notifySuccess } from '../../utils/Notify';
import { BeatLoader } from 'react-spinners';
export const PhoneNumberVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { phoneNumber, userExists } = location.state || {};
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const OTP_DURATION = 300;
  const getRemainingTime = () => {
    const sentAt = sessionStorage.getItem('otp_sent_at');
    if (!sentAt) return OTP_DURATION;
    const elapsed = Math.floor((Date.now() - Number(sentAt)) / 1000);
    return Math.max(0, OTP_DURATION - elapsed);
  };

  const [countdown, setCountdown] = useState(getRemainingTime);
  const timerRef = useRef(null);

  const startTimer = useCallback((reset = false) => {
    if (reset) {
      sessionStorage.setItem('otp_sent_at', Date.now().toString());
      setCountdown(OTP_DURATION);
    } else {
      setCountdown(getRemainingTime());
    }
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          sessionStorage.removeItem('otp_verification_id');
          sessionStorage.removeItem('otp_sent_at');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (!sessionStorage.getItem('otp_sent_at')) {
      sessionStorage.setItem('otp_sent_at', Date.now().toString());
    }
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startTimer]);

  const formatTimer = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!phoneNumber || userExists) {
      navigate(PHONE_PASSWORD_LOGIN);
    }
  }, [phoneNumber, userExists, navigate]);

  useEffect(() => {
    if (!window.confirmationResult && !sessionStorage.getItem('otp_verification_id')) {
      setError('Session expirée. Recommence depuis le début.');
      setTimeout(() => {
        navigate(PHONE_PASSWORD_LOGIN);
      }, 2000);
    }
  }, [navigate]);

  const displayNumber = phoneNumber ? formatPhoneNumber(phoneNumber) : '';

  const handleVerifyCode = async (otp) => {
    if (otp.length !== 6) return;

    setError('');

    const result = await handleOtpVerification({
      auth,
      otp,
      setLoading,
      onSuccess: () => {
        navigate(PHONE_PASSWORD_CREATE, {
          replace: true,
          state: {
            phoneNumber,
            isForgotPassword: false,
            isVerified: true,
            isNewUser: true,
          },
        });
      },
      onError: (errorMessage) => {
        if (errorMessage?.includes('code-expired')) {
          setError('Code expiré. Demande un nouveau code.');
          sessionStorage.removeItem('otp_verification_id');
          sessionStorage.removeItem('otp_sent_at');
          setTimeout(() => navigate(PHONE_PASSWORD_LOGIN), 3000);
        } else if (errorMessage?.includes('invalid-verification-code')) {
          setError('Code incorrect. Réessaie.');
        } else {
          setError('Erreur de vérification. Réessaie.');
        }
        setCode('');
      },
    });

    if (!result.success) {
      setCode('');
    }
  };

  useEffect(() => {
    if (code.length === 6) {
      handleVerifyCode(code);
    }
  }, [code]);

  const handleResendSMS = async () => {
    if (!phoneNumber || resending) return;

    setResending(true);
    setError('');
    setCode('');

    try {
      const result = await phoneNumberSignUp(
        auth,
        phoneNumber,
        null,
        'signup',
        'phone-verify-resend-recaptcha',
        setResending
      );

      if (result?.success) {
        notifySuccess('Code renvoyé avec succès');
        startTimer(true);
        setResending(false);
      } else {
        setError('Erreur lors du renvoi du code. Réessaie.');
        setResending(false);
      }
    } catch (error) {
      console.error('Resend SMS error:', error);
      setError('Erreur lors du renvoi du code. Réessaie.');
      setResending(false);
    }
  };

  return (
    <PageWrapper
      title="👀 Code envoyé par SMS"
      onGoBack={() => {
        sessionStorage.removeItem('otp_verification_id');
        sessionStorage.removeItem('otp_sent_at');
        navigate(HOME);
      }}
    >
      <div className="flex flex-col items-center justify-center gap-4">
        {loading ? (
          <div className="flex flex-col items-center gap-4">
            <BeatLoader color="#183153" size={20} />
            <p className="text-gray-500 text-lg">Vérification en cours...</p>
          </div>
        ) : (
          <>
            <p className="text-start w-full text-xl text-gray-800 geomanistRegular mb-6">
              Envoyé au <span className="geomanistMedium">{displayNumber}</span>
            </p>

            <OtpInput
              value={code}
              onChange={setCode}
              numInputs={6}
              isInputNum={true}
              isDisabled={resending}
              shouldAutoFocus={true}
              inputStyle="password-input"
              isInputSecure={false}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              separator={<span className="otp-separator" />}
              containerStyle="otp-container"
            />

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4 w-full">
                <p className="text-red-600 text-lg font-semibold text-center">{error}</p>
              </div>
            )}

            {countdown > 0 && (
              <p className="text-gray-500 text-xl mt-4 text-center">
                Le code expire dans <span className="font-semibold">{formatTimer(countdown)}</span>
              </p>
            )}

            {countdown === 0 && (
              <p className="text-red-500 text-xl mt-4 text-center">
                Code expiré. Renvoie un nouveau SMS.
              </p>
            )}

            <button
              type="button"
              className="geomanistMedium mt-6 text-xl p-4 underline rounded-full w-full disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleResendSMS}
              disabled={resending}
            >
              {resending ? 'Envoi en cours...' : 'Renvoyer le SMS'}
            </button>
          </>
        )}
      </div>
      {/* Hidden reCAPTCHA container for resend */}
      <div id="phone-verify-resend-recaptcha" style={{ display: 'none' }} />
    </PageWrapper>
  );
};

